/**
 * Display MageWorx APO conditional price adjustments for Frame Style cards.
 *
 * APO already owns option selection, total calculation, and cart data. This
 * adapter only reads APO's loaded response and mirrors matching rule amounts
 * into the Frame Style UI.
 */
(function () {
  'use strict';

  if (window.__LP_APO_FRAME_PRICE__) return;
  window.__LP_APO_FRAME_PRICE__ = true;

  var FRAME_SELECTOR =
    '.main-product .product__info-container .lp-frame-option';
  var INFO_SELECTOR = '.main-product .product__info-container';
  var CARD_PRICE_ATTRIBUTE = 'data-lp-frame-rule-price';
  var SUMMARY_PRICE_ATTRIBUTE = 'data-lp-frame-rule-price-summary';
  var ZERO_PRICE_ATTRIBUTE = 'data-lp-zero-option-price';

  // "Matching Pet Gifts" option. APO writes the accumulated selected-values
  // line itself, so the only stable hooks are the attribute below and the
  // option's own title text. Renaming the option in the APO admin silently
  // disables this.
  var ACCUMULATED_SELECTOR = '[data-mw-option-accumulated-title]';
  var TARGET_OPTION_TITLE = 'Matching Pet Gifts';
  var ACCUMULATED_CLASS = 'lp-accumulated';
  var ACCUMULATED_ITEM_CLASS = 'lp-accumulated__item';
  var ENTRY_SEPARATOR = ', ';

  var renderTimer = null;
  var accumulatedTimer = null;
  var retryTimer = null;
  var retryCount = 0;
  var eventVariantId = '';

  function valuesOf(value) {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    if (typeof value === 'object') {
      return Object.keys(value).map(function (key) {
        return value[key];
      });
    }
    return [value];
  }

  function uniqueStrings(values) {
    return values.reduce(function (result, value) {
      var normalized = String(value || '').trim();
      if (normalized && result.indexOf(normalized) === -1) {
        result.push(normalized);
      }
      return result;
    }, []);
  }

  function getOptionId(option) {
    if (!option) return '';
    return String(
      option.getAttribute('data-option-id') || option.getAttribute('id') || ''
    ).trim();
  }

  function getValueId(valueCard) {
    if (!valueCard) return '';

    var valueId = valueCard.getAttribute('data-value-id');
    if (valueId) return String(valueId).trim();

    var control = valueCard.querySelector('.mw-option__control');
    return control && control.value ? String(control.value).trim() : '';
  }

  function getCurrentVariantId(init) {
    if (eventVariantId) return eventVariantId;

    var info = document.querySelector(INFO_SELECTOR);
    var variantInput = info
      ? info.querySelector(
          'form[action*="/cart/add"] [name="id"], input[name="id"]'
        )
      : null;

    if (variantInput && variantInput.value) {
      return String(variantInput.value);
    }

    var product = init && init.product;
    var selectedVariant = product && product.selected_or_first_available_variant;
    if (selectedVariant && selectedVariant.id) {
      return String(selectedVariant.id);
    }

    if (product && product.variants && product.variants[0]) {
      return String(product.variants[0].id);
    }

    return '';
  }

  function getApoResponse() {
    var init = window.mwApoInit;
    var responses = init && init.response;
    if (!responses || typeof responses !== 'object') return null;

    var variantId = getCurrentVariantId(init);
    if (variantId && responses[variantId]) return responses[variantId];

    var keys = Object.keys(responses);
    if (keys.length === 1) return responses[keys[0]];

    for (var i = 0; i < keys.length; i += 1) {
      var response = responses[keys[i]];
      if (response && String(response.variant_id) === variantId) {
        return response;
      }
    }

    return null;
  }

  function collectSelectedValues(info) {
    var selectedByOption = {};
    var options = info.querySelectorAll(
      '.mw-option[data-option-id], .mw-option[id^="o_"]'
    );

    options.forEach(function (option) {
      var optionId = getOptionId(option);
      if (!optionId) return;

      var selected = [];

      option
        .querySelectorAll(
          'input.mw-option__control:checked, option:checked'
        )
        .forEach(function (control) {
          if (control.value) selected.push(control.value);
        });

      option
        .querySelectorAll('.mw-option__value--selected[data-value-id]')
        .forEach(function (value) {
          selected.push(value.getAttribute('data-value-id'));
        });

      selectedByOption[optionId] = uniqueStrings(selected);
    });

    return selectedByOption;
  }

  function conditionMatches(
    condition,
    response,
    selectedByOption,
    frameOptionId,
    frameValueId
  ) {
    if (!condition || !condition.option_id) return false;

    var optionId = String(condition.option_id);
    var selected =
      optionId === frameOptionId
        ? [frameValueId]
        : selectedByOption[optionId] || [];
    var expected = uniqueStrings(valuesOf(condition.values));
    if (!expected.length) return false;

    var option =
      response && response.options && response.options[optionId]
        ? response.options[optionId]
        : null;
    var inputType = String((option && option.input_type) || '').toLowerCase();
    var isMultiValue =
      inputType.indexOf('multi') === 0 ||
      inputType === 'checkbox' ||
      inputType === 'multiselect';
    var hasMatch = isMultiValue
      ? expected.every(function (value) {
          return selected.indexOf(String(value)) !== -1;
        })
      : expected.some(function (value) {
          return selected.indexOf(String(value)) !== -1;
        });
    var hasExcludedMatch = expected.some(function (value) {
      return selected.indexOf(String(value)) !== -1;
    });
    var operator = String(condition.operator || 'eq').toLowerCase();

    if (
      operator === '!eq' ||
      operator === 'neq' ||
      operator === 'not_eq' ||
      operator === '!=' ||
      operator === 'notin'
    ) {
      return !hasExcludedMatch;
    }

    if (
      operator === 'eq' ||
      operator === '=' ||
      operator === '==' ||
      operator === 'in'
    ) {
      return hasMatch;
    }

    return false;
  }

  function ruleMatches(
    rule,
    response,
    selectedByOption,
    frameOptionId,
    frameValueId
  ) {
    var conditions = valuesOf(rule && rule.conditions);
    if (!conditions.length) return false;

    var frameConditions = conditions.filter(function (condition) {
      return String(condition.option_id || '') === frameOptionId;
    });
    if (!frameConditions.length) return false;

    var matcher = function (condition) {
      return conditionMatches(
        condition,
        response,
        selectedByOption,
        frameOptionId,
        frameValueId
      );
    };

    var frameMatches = frameConditions.some(matcher);
    if (!frameMatches) return false;

    return String(rule.condition_type || 'all').toLowerCase() === 'any'
      ? conditions.some(matcher)
      : conditions.every(matcher);
  }

  function calculateAdjustment(
    response,
    selectedByOption,
    frameOptionId,
    frameValueId
  ) {
    return valuesOf(response && response.rules).reduce(function (total, entry) {
      var rule = entry && entry.rule ? entry.rule : entry;
      if (
        !rule ||
        !ruleMatches(
          rule,
          response,
          selectedByOption,
          frameOptionId,
          frameValueId
        )
      ) {
        return total;
      }

      var price = rule.price || {};
      var amount = Number(price.value);
      if (!Number.isFinite(amount) || amount === 0) return total;

      var mode = String(price.mode || '').toLowerCase();
      if (mode === 'increase') return total + amount;
      if (mode === 'decrease') return total - amount;

      // A "set" rule replaces a total; it is not a Frame Style surcharge.
      return total;
    }, 0);
  }

  function formatNumber(cents, placeholder) {
    var precision = placeholder.indexOf('no_decimals') !== -1 ? 0 : 2;
    var thousands = ',';
    var decimal = '.';

    if (placeholder.indexOf('comma_separator') !== -1) {
      thousands = '.';
      decimal = ',';
    } else if (placeholder.indexOf('space_separator') !== -1) {
      thousands = ' ';
      decimal = ',';
    } else if (placeholder.indexOf('apostrophe_separator') !== -1) {
      thousands = "'";
    }

    var number = (cents / 100).toFixed(precision).split('.');
    var whole = number[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
    return whole + (number[1] ? decimal + number[1] : '');
  }

  function formatMoney(amount) {
    var init = window.mwApoInit || {};
    var shopCurrency = init.shop_currency || '';
    var activeCurrency =
      (window.Shopify &&
        Shopify.currency &&
        Shopify.currency.active) ||
      shopCurrency;
    var rate = Number(
      window.Shopify && Shopify.currency && Shopify.currency.rate
    );
    if (!Number.isFinite(rate) || rate <= 0) rate = 1;

    var convertedAmount = Math.abs(amount) * rate;
    var cents = Math.round(convertedAmount * 100);
    if (window.LPMoney && typeof window.LPMoney.format === 'function') {
      return window.LPMoney.format(cents, {
        currency: activeCurrency || shopCurrency,
        locale: document.documentElement.lang || 'en'
      });
    }

    var settings = init.settings || {};
    var includeCurrency =
      settings.currency_html_format &&
      settings.currency_html_format !== 'without_currency';
    var moneyFormat = includeCurrency
      ? init.shop_money_with_currency_format
      : init.shop_money_format;

    if (
      activeCurrency === shopCurrency &&
      moneyFormat &&
      moneyFormat.indexOf('<') === -1
    ) {
      var placeholderMatch = moneyFormat.match(/\{\{\s*(\w+)\s*\}\}/);
      if (placeholderMatch) {
        return moneyFormat.replace(
          placeholderMatch[0],
          formatNumber(cents, placeholderMatch[1])
        );
      }
    }

    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: activeCurrency || shopCurrency || 'USD'
      }).format(cents / 100);
    } catch (error) {
      return (activeCurrency || shopCurrency || '$') + (cents / 100).toFixed(2);
    }
  }

  function formatAdjustment(amount) {
    if (Math.abs(amount) < 0.005) return '';
    return (amount > 0 ? '+' : '-') + formatMoney(amount);
  }

  function hasNativePrice(valueCard) {
    return Array.prototype.some.call(
      valueCard.querySelectorAll('.mw-option__price'),
      function (price) {
        return !price.closest('[' + ZERO_PRICE_ATTRIBUTE + ']') &&
          price.textContent.trim().length > 0;
      }
    );
  }

  function updateCardPrice(valueCard, priceText) {
    var priceWrapper = valueCard.querySelector(
      '[' + CARD_PRICE_ATTRIBUTE + ']'
    );

    if (!priceText || hasNativePrice(valueCard)) {
      if (priceWrapper) priceWrapper.remove();
      return false;
    }

    if (!priceWrapper) {
      var content = valueCard.querySelector('.mw-stack--wide__content');
      if (!content) return false;

      priceWrapper = document.createElement('div');
      priceWrapper.className =
        'mw-stack--wide__content-price lp-frame-rule-price-wrap';
      priceWrapper.setAttribute(CARD_PRICE_ATTRIBUTE, '');

      var price = document.createElement('span');
      price.className = 'mw-text lp-frame-rule-price';
      priceWrapper.appendChild(price);
      content.appendChild(priceWrapper);
    }

    var priceNode = priceWrapper.querySelector('.lp-frame-rule-price');
    if (priceNode && priceNode.textContent !== priceText) {
      priceNode.textContent = priceText;
    }

    return true;
  }

  function isSelected(valueCard) {
    var control = valueCard.querySelector('.mw-option__control');
    return Boolean(
      (control && control.checked) ||
        valueCard.classList.contains('mw-option__value--selected')
    );
  }

  function updateSummary(frameOption, priceText) {
    var summaryPrice = frameOption.querySelector(
      '[' + SUMMARY_PRICE_ATTRIBUTE + ']'
    );

    if (!priceText) {
      if (summaryPrice) summaryPrice.remove();
      return;
    }

    if (!summaryPrice) {
      var selectedTitle = frameOption.querySelector(
        '[data-mw-option-accumulated-title]'
      );
      if (!selectedTitle || !selectedTitle.parentNode) return;

      summaryPrice = document.createElement('span');
      summaryPrice.className = 'mw-text lp-frame-rule-price-summary';
      summaryPrice.setAttribute(SUMMARY_PRICE_ATTRIBUTE, '');
      selectedTitle.parentNode.insertBefore(
        summaryPrice,
        selectedTitle.nextSibling
      );
    }

    var summaryText = '(' + priceText + ')';
    if (summaryPrice.textContent !== summaryText) {
      summaryPrice.textContent = summaryText;
    }
  }

  function renderFramePrices(response) {
    var info = document.querySelector(INFO_SELECTOR);
    if (!info) return;

    var selectedByOption = collectSelectedValues(info);
    info.querySelectorAll('.lp-frame-option').forEach(function (frameOption) {
      var frameOptionId = getOptionId(frameOption);
      if (!frameOptionId) return;

      var selectedPriceText = '';
      frameOption
        .querySelectorAll('.mw-option__value[data-value-id]')
        .forEach(function (valueCard) {
          var frameValueId = getValueId(valueCard);
          var adjustment = frameValueId
            ? calculateAdjustment(
                response,
                selectedByOption,
                frameOptionId,
                frameValueId
              )
            : 0;
          var priceText = formatAdjustment(adjustment);
          var displayed = updateCardPrice(valueCard, priceText);

          if (displayed && isSelected(valueCard)) {
            selectedPriceText = priceText;
          }
        });

      updateSummary(frameOption, selectedPriceText);
    });
  }

  function renderZeroPrices(response) {
    if (!document.body.classList.contains('lp-portrait-pdp')) return;
    var info = document.querySelector(INFO_SELECTOR);
    if (!info) return;
    var selectedByOption = collectSelectedValues(info);

    info.querySelectorAll('.mw-option--type-wideimagecard').forEach(function (option) {
      var optionId = getOptionId(option);
      var definition = response.options && response.options[optionId];
      if (!definition || definition.enable_formula_based_price) return;
      var title = String(definition.title || '').trim();
      var isFrame = option.classList.contains('lp-frame-option') || title === 'Frame Style';
      var isSize = option.classList.contains('lp-size-guide-2d') ||
        option.classList.contains('lp-size-guide-3d') || title === 'Portrait Size';
      if (!isFrame && !isSize) return;

      option.querySelectorAll('.mw-option__value[data-value-id]').forEach(function (card) {
        var zero = card.querySelector('[' + ZERO_PRICE_ATTRIBUTE + ']');
        var valueId = getValueId(card);
        var entry = response.values && response.values[valueId];
        var value = entry && entry.value;
        var pricing = value && value.pricing;
        var basePrice = pricing && pricing.price;
        var amount = basePrice == null || basePrice === '' ? 0 : Number(basePrice);
        var rulePrice = isFrame ? calculateAdjustment(response, selectedByOption, optionId, valueId) : 0;
        var eligible = value && !value.link_variant && Number.isFinite(amount) &&
          amount === 0 && rulePrice === 0 && !hasNativePrice(card) &&
          !card.querySelector('[' + CARD_PRICE_ATTRIBUTE + ']');
        if (!eligible) {
          if (zero) zero.remove();
          return;
        }
        if (!zero) {
          var content = card.querySelector('.mw-stack--wide__content');
          if (!content) return;
          zero = document.createElement('div');
          zero.className = 'mw-stack--wide__content-price';
          zero.setAttribute(ZERO_PRICE_ATTRIBUTE, '');
          var price = document.createElement('span');
          price.className = 'mw-text mw-option__price';
          zero.appendChild(price);
          content.appendChild(zero);
        }
        var text = '+' + formatMoney(0);
        if (zero.firstChild.textContent !== text) zero.firstChild.textContent = text;
      });
    });
  }

  /**
   * APO's accumulated selected-values line. On every option change APO
   * rebuilds it with `container.innerHTML = ...`, in this exact shape:
   *
   *   Title&nbsp;<span class="mw-text mw-option__price">(+$85.00)</span>
   *   &nbsp;<s class="mw-text mw-option__compare-price">(+$113.00)</s>
   *   + a bare ", " text node + the next entry
   *
   * Titles and separators are bare text nodes, so CSS can neither address them
   * nor delete the separator; a forced line break would leave ", Matching Mug"
   * stranded at the start of the next line. This rewrites the line into one
   * element per entry and drops the separators. Layout stays in CSS
   * (assets/section-main-product.css, .lp-accumulated).
   */
  function wrapAccumulated(container) {
    var nodes = Array.prototype.slice.call(container.childNodes);

    // Idempotence is required, not cosmetic: the observer below also sees our
    // own insertions. Once wrapped there is no top-level separator left, so the
    // second pass returns here and the mutation cycle settles.
    var hasSeparator = nodes.some(function (node) {
      return (
        node.nodeType === 3 && node.nodeValue.indexOf(ENTRY_SEPARATOR) !== -1
      );
    });
    if (!hasSeparator) return false;

    var groups = [];
    var current = null;

    nodes.forEach(function (node) {
      if (node.nodeType !== 3) {
        if (!current) {
          current = [];
          groups.push(current);
        }
        current.push(node);
        return;
      }

      // Split a run of text into entries. A separator text node holds nothing
      // but ", " + the next title, so the part before it is empty and the
      // original node is simply never re-appended; the while loop below then
      // clears it. That is how the commas disappear.
      node.nodeValue.split(ENTRY_SEPARATOR).forEach(function (part, index) {
        if (index > 0) current = null;
        if (!part) return;
        if (!current) {
          current = [];
          groups.push(current);
        }
        current.push(document.createTextNode(part));
      });
    });

    if (groups.length < 2) return false;

    var fragment = document.createDocumentFragment();
    groups.forEach(function (group) {
      var item = document.createElement('span');
      item.className = ACCUMULATED_ITEM_CLASS;
      group.forEach(function (node) {
        item.appendChild(node);
      });
      fragment.appendChild(item);
    });

    while (container.firstChild) container.removeChild(container.firstChild);
    container.appendChild(fragment);

    return true;
  }

  /**
   * The option's own title is rendered inline right before the accumulated
   * line, so the immediate parent is normally the tightest scope that holds
   * both. Matching there cannot leak into a neighbouring option the way a
   * document-wide text search would.
   */
  function isTargetOption(container) {
    var parent = container.parentElement;
    if (parent && parent.textContent.indexOf(TARGET_OPTION_TITLE) !== -1) {
      return true;
    }

    // Fallback for a layout that keeps the title further up: .mw-option is the
    // option root, which still cannot span two options.
    var option = container.closest('.mw-option');
    return Boolean(
      option && option.textContent.indexOf(TARGET_OPTION_TITLE) !== -1
    );
  }

  function applyAccumulatedLayout() {
    accumulatedTimer = null;

    var info = document.querySelector(INFO_SELECTOR);
    if (!info) return;

    info.querySelectorAll(ACCUMULATED_SELECTOR).forEach(function (container) {
      if (!isTargetOption(container)) return;
      container.classList.add(ACCUMULATED_CLASS);
      wrapAccumulated(container);
    });
  }

  function scheduleAccumulated(delay) {
    if (accumulatedTimer) window.clearTimeout(accumulatedTimer);
    accumulatedTimer = window.setTimeout(
      applyAccumulatedLayout,
      delay == null ? 60 : delay
    );
  }

  function refresh() {
    renderTimer = null;

    if (!document.querySelector(FRAME_SELECTOR) &&
        !document.querySelector('body.lp-portrait-pdp ' + INFO_SELECTOR + ' .mw-option')) return;

    var response = getApoResponse();
    if (response) {
      retryCount = 0;
      renderFramePrices(response);
      renderZeroPrices(response);
      return;
    }

    if (retryCount < 40 && !retryTimer) {
      retryCount += 1;
      retryTimer = window.setTimeout(function () {
        retryTimer = null;
        scheduleRefresh(0);
      }, 250);
    }
  }

  function scheduleRefresh(delay) {
    if (renderTimer) window.clearTimeout(renderTimer);
    renderTimer = window.setTimeout(refresh, delay == null ? 60 : delay);
  }

  function mutationTouchesApo(mutation) {
    var target =
      mutation.target.nodeType === Node.ELEMENT_NODE
        ? mutation.target
        : mutation.target.parentElement;

    if (
      (document.querySelector(FRAME_SELECTOR) || document.body.classList.contains('lp-portrait-pdp')) &&
      target &&
      target.closest &&
      target.closest('.mw-option')
    ) {
      return true;
    }

    return Array.prototype.some.call(mutation.addedNodes, function (node) {
      return Boolean(
        node.nodeType === Node.ELEMENT_NODE &&
          (node.matches('.lp-frame-option') ||
            node.querySelector('.lp-frame-option'))
      );
    });
  }

  function handleApoEvent(event) {
    var variantId = event && event.detail && event.detail.variantId;
    if (variantId) eventVariantId = String(variantId);
    scheduleRefresh(0);
  }

  function bind() {
    var info = document.querySelector(INFO_SELECTOR);
    if (!info) return;

    document.addEventListener('mwApo::ready', handleApoEvent);
    document.addEventListener('mwApo::recalculated', handleApoEvent);
    document.addEventListener('luminipet:apo-ready', handleApoEvent);
    document.addEventListener('variant:change', function (event) {
      var variant = event && event.detail && event.detail.variant;
      eventVariantId = variant && variant.id ? String(variant.id) : '';
      scheduleRefresh(0);
    });

    info.addEventListener('change', function (event) {
      var target = event.target;
      if (!target) return;

      if (target.closest('.mw-option')) {
        scheduleRefresh(40);
      } else if (target.matches('[name="id"]')) {
        eventVariantId = String(target.value || '');
        scheduleRefresh(40);
      }
    });

    if ('MutationObserver' in window) {
      new MutationObserver(function (mutations) {
        if (mutations.some(mutationTouchesApo)) scheduleRefresh(80);
      }).observe(info, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'aria-checked']
      });

      // The accumulated line is not the Frame Style option's business, and
      // refresh() bails out entirely on a product without .lp-frame-option, so
      // it needs its own observer rather than a branch inside the one above.
      // childList alone is enough: APO only ever replaces the line's contents.
      new MutationObserver(function () {
        // Normalize APO's rewritten gift summary in this microtask, before
        // paint. A timer lets the original comma-separated prices flash.
        // wrapAccumulated is idempotent, so our own mutations settle.
        applyAccumulatedLayout();
      }).observe(info, { childList: true, subtree: true });
    }

    scheduleRefresh(0);
    scheduleAccumulated(0);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bind, { once: true });
  } else {
    bind();
  }
})();
